import { DestroyRef, inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import {
  authenticateByContractNumber,
  authenticateByUserNamePassword,
  selectAuthenticationResult,
  selectHasAcceptedConsent
} from '../store';
import { loadEnvironment, selectEnvironment } from '@qvs/core/env';
import { AuthenticatorService } from '~/shared/services/authenticator.service';
import { AuthenticationResult } from '@qvs/core';
import { AuthenticationResponse } from '@wize-mobile-api';
import { HttpClient } from '@angular/common/http';
import { environment } from '#environment';
import { ConfigurationEnum, EnvironmentEnum } from '@environments/IEnvironment';
import { AlertController } from '@ionic/angular';
import { jwtDecode } from 'jwt-decode';
import { DebugService } from '~/shared/services/debug.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type Token = string;

export interface JwtPayload {
  scopes: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authResult: AuthenticationResult;
  accepted: boolean;
  scopes: string[];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly store: Store,
    private readonly authenticator: AuthenticatorService,
    private readonly alertController: AlertController,
    private readonly httpClient: HttpClient,
    private readonly debugService: DebugService
  ) {
    this.store.pipe(select(selectAuthenticationResult)).subscribe((result) => {
      this.authResult = result;
      // TODO 15-May-2026/braune (MOB-1279): chatToken & accessToken should be merged
      this.scopes = this.decodeJwtScopes(result?.accessToken ?? result?.chatToken);
    });
    this.store.pipe(select(selectHasAcceptedConsent)).subscribe((result) => (this.accepted = result));
  }

  public authenticateByContractNumber(contractNumber: string): Observable<AuthenticationResult> {
    return this.store.select(selectEnvironment).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((env) => env?.authenticationBasePath !== undefined),
      take(1),
      switchMap((env) => this.authenticator.authenticate(contractNumber, env)),
      catchError((err) => throwError(() => err))
    );
  }

  public authenticateByUserPassword(username: string, password: string): Observable<Token> {
    return this.store.select(selectEnvironment).pipe(
      takeUntilDestroyed(this.destroyRef),
      take(1),
      switchMap((env) =>
        // TODO 06-Jan-2023/braune: this is not in any API definition atm.
        this.httpClient.post<AuthenticationResponse>(`${env.authenticationBasePath}restful/api/auth/v1/authenticate`, null, {
          headers: { Authorization: `Basic ${btoa(username + ':' + password)}` }
        })
      ),
      map((response) => response.accessToken)
    );
  }

  async receiveContractNumber(contractNumber: string) {
    if (!contractNumber) {
      return;
    }

    const contractNumberPrefixed = contractNumber;
    const contractNumberPatterns = environment.contractNumberPatterns;
    const demoMatch = contractNumberPatterns.demo.exec(contractNumber);
    const sandboxMatch = contractNumberPatterns.sandbox.exec(contractNumber);
    const uatMatch = contractNumberPatterns.uat.exec(contractNumber);
    const testMatch = contractNumberPatterns.test.exec(contractNumber);
    const contractNumberCaptureGroupIndex = 1;
    if (demoMatch || sandboxMatch) {
      if (demoMatch) {
        this.store.dispatch(loadEnvironment({ environmentConfiguration: ConfigurationEnum.DEMO }));
      } else if (sandboxMatch) {
        if (![ConfigurationEnum.TEST_LOCAL, ConfigurationEnum.UAT_LOCAL].includes(environment.configuration)) {
          this.store.dispatch(loadEnvironment({ environmentConfiguration: ConfigurationEnum.SANDBOX }));
        }
        contractNumber = sandboxMatch[contractNumberCaptureGroupIndex];
      }
      const alert = await this.alertController.create({
        header: 'Api Auth',
        inputs: [
          {
            name: 'password',
            type: 'password',
            placeholder: 'Password'
          }
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary'
          },
          {
            text: 'Ok',
            handler: (event) => {
              setTimeout(() => {
                this.store.dispatch(
                  authenticateByUserNamePassword({
                    username: contractNumber,
                    password: event.password,
                    usernamePrefixed: contractNumberPrefixed
                  })
                );
              }, 100);
            }
          }
        ]
      });
      return alert.present();
    } else if (uatMatch) {
      this.store
        .select(selectEnvironment)
        .pipe(
          filter((env) => env.deploymentEnvironment === EnvironmentEnum.UAT),
          take(1)
        )
        .subscribe(() =>
          this.store.dispatch(
            authenticateByContractNumber({
              contractNumber: uatMatch[contractNumberCaptureGroupIndex],
              contractNumberPrefixed
            })
          )
        );

      this.store.dispatch(loadEnvironment({ environmentConfiguration: ConfigurationEnum.UAT }));
      return;
    } else if (testMatch) {
      this.store
        .select(selectEnvironment)
        .pipe(
          filter((env) => env.deploymentEnvironment === EnvironmentEnum.TEST),
          take(1)
        )
        .subscribe(() =>
          this.store.dispatch(
            authenticateByContractNumber({
              contractNumber: testMatch[contractNumberCaptureGroupIndex],
              contractNumberPrefixed
            })
          )
        );

      this.store.dispatch(loadEnvironment({ environmentConfiguration: ConfigurationEnum.TEST }));
      return;
    }

    this.store.dispatch(authenticateByContractNumber({ contractNumber, contractNumberPrefixed }));
  }

  logout(authenticationResult: AuthenticationResult): Observable<void> {
    return this.authenticator.logout(authenticationResult);
  }

  decodeJwtScopes(token?: string): string[] {
    if (token?.split('.').length !== 3) {
      return [];
    }

    try {
      const decoded = this.decodeToken(token);
      return decoded.scopes || [];
    } catch (error) {
      this.debugService.logMessage(error?.message ?? error);
      return [];
    }
  }

  public isScopeAuthorized(scope: string): boolean {
    return this.scopes?.some((s) => s.includes(scope));
  }

  public isAuthenticated(): boolean {
    return this.authResult.isAuthenticated;
  }

  public hasAcceptedTOU() {
    return this.accepted;
  }

  private decodeToken(token: string): JwtPayload {
    return jwtDecode<JwtPayload>(token);
  }
}
